import { CallDto, VoiceCallAuditDto } from '@act/shared/data-transfer-objects';
import {
  Box,
  CardActions,
  CardContent,
  Dialog,
  Divider,
  Typography,
} from '@mui/material';
import { Fragment, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { callsRequests } from 'redux/requests/calls.request';
import { userRequests } from 'redux/requests/users.request';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from '../dialog-forms/components/SharedDialogHookComponents';
import { Button } from '@act/storybook';
import { ControlledAutocomplete } from '@act/storybook';
import {
  loadTeamHierarchy,
  selectHierarchy,
  selectHierarchyLoadingStatus,
  selectTeamLoadingStatus,
} from 'redux/slices/usersSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import Loader from '../workflows/Loader';

export interface AssignCallAuditorUniqueProps {
  call: CallDto;
  callsReviewTasks: VoiceCallAuditDto[];
}

export type AssignCallAuditorDialogProps = GenericDialogProps<boolean> &
  AssignCallAuditorUniqueProps;

export interface AssignCallAuditorForm {
  assigneeIds: string[];
  auditType: string;
}

const AssignCallAuditorDialog = ({
  call,
  open,
  setOpen,
  callback,
  callsReviewTasks,
}: AssignCallAuditorDialogProps) => {
  const dispatch = useAppDispatch();
  const hierarchyLoadingStatus = useAppSelector(selectHierarchyLoadingStatus);
  const teamLoadingStatus = useAppSelector(selectTeamLoadingStatus);
  const teamHierarchy = useAppSelector(selectHierarchy);

  const [teamUsersResponse, isLoadingGuides, getTeamUsers] = useApiCall(
    userRequests.getTeam
  );

  // Transform team users into options
  const guideOptions = useMemo(() => {
    if (!teamUsersResponse) {
      return [];
    }

    // Filter out duplicates based on guide ID to prevent duplicates
    const uniqueGuides = teamUsersResponse.filter(
      (guide, index, self) => index === self.findIndex(g => g.id === guide.id)
    );

    return uniqueGuides.map(guide => ({
      label: guide.name,
      value: guide.id,
    }));
  }, [teamUsersResponse]);

  useEffect(() => {
    if (
      teamHierarchy?.length &&
      teamLoadingStatus !== 'loading' &&
      !teamUsersResponse
    ) {
      getTeamUsers({ params: { allViewable: true } });
    }
  }, [teamHierarchy, teamLoadingStatus, teamUsersResponse, getTeamUsers]);

  const [_updatedCall, _isUpdatingCall, createCallReviewTask] = useApiCall(
    callsRequests.createCallReviewTask
  );

  useEffect(() => {
    async function fetchTeamHierarchy() {
      await dispatch(await loadTeamHierarchy());
    }

    if (teamHierarchy === null && hierarchyLoadingStatus !== 'loading') {
      void fetchTeamHierarchy();
    }
  }, [teamHierarchy, hierarchyLoadingStatus, dispatch]);

  const {
    control,
    getValues,
    setValue,
    formState: { isValid },
  } = useForm<AssignCallAuditorForm>({
    defaultValues: {
      assigneeIds: callsReviewTasks?.map(task => task.assigneeId) || [],
      auditType: 'voicemail_review',
    },
  });

  // Update form values when guideIds become available
  useEffect(() => {
    if (
      callsReviewTasks?.map(task => task.assigneeId) &&
      callsReviewTasks?.map(task => task.assigneeId).length > 0
    ) {
      setValue(
        'assigneeIds',
        callsReviewTasks?.map(task => task.assigneeId)
      );
    }
  }, [callsReviewTasks, setValue]);

  function handleClose() {
    setOpen(false);
    callback(false);
  }

  async function handleSubmit() {
    const { assigneeIds, auditType } = getValues();

    void createCallReviewTask({
      params: { callId: call?.id },
      body: { callId: call?.id, assigneeIds, auditType },
    });

    handleClose();
  }

  return (
    <div style={{ width: '600px' }}>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogContentContainer>
          <DialogTitleBarComponent
            title="Share Call"
            handleClose={handleClose}
          />
          <CardContent sx={{ padding: '12px 24px 0' }}>
            <Fragment>
              <Typography variant="titleMedium">
                Select a community guide to share this call with
              </Typography>
              <Box sx={{ margin: '8px 0 16px' }}>
                {(!teamUsersResponse || isLoadingGuides) && <Loader />}
                {teamUsersResponse && teamUsersResponse.length > 0 && (
                  <ControlledAutocomplete
                    control={control}
                    name="assigneeIds"
                    label="Select Guides"
                    options={guideOptions}
                    getOptionLabel={option => option.label}
                    multiple={true}
                    required
                  />
                )}
                {teamUsersResponse && teamUsersResponse.length === 0 && (
                  <Typography variant="bodyMedium" color="text.secondary">
                    No guides available for assignment.
                  </Typography>
                )}
              </Box>
            </Fragment>
          </CardContent>
          <Divider sx={{ marginTop: '48px' }} />
          <CardActions
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'right',
              padding: '8px 24px',
            }}
          >
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              disabled={!isValid}
              debounced
              onClick={handleSubmit}
              type={'submit'}
              variant="contained"
            >
              Share call
            </Button>
          </CardActions>
        </DialogContentContainer>
      </Dialog>
    </div>
  );
};

export default AssignCallAuditorDialog;
